import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractErrorMessage } from '../../core/services/api';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import type { Tenant } from '../../core/types/data-models';

interface DescopeRole {
  name: string;
  description: string;
  tenantId: string;
}

interface TenantAssignment {
  tenantId: string;
  roles: string[];
}

interface CreateUserPayload {
  email: string;
  displayName?: string;
  roles?: string[];
  tenants?: TenantAssignment[];
  sendInvite: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CreateUserDialog({ open, onClose }: Props) {
  const qc = useQueryClient();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [tenantAssignments, setTenantAssignments] = useState<TenantAssignment[]>([]);
  const [sendInvite, setSendInvite] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load all tenants for the tenant selector
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants', 'for-user-dialog'],
    queryFn: () => api.get<{ data: Tenant[] }>('/tenants').then((r) => r.data.data),
    enabled: open,
    staleTime: 60_000,
  });
  const tenants = tenantsData ?? [];

  // Load available Descope roles for role assignment
  const { data: rolesData } = useQuery({
    queryKey: ['descope-management', 'roles'],
    queryFn: () =>
      api.get<{ data: DescopeRole[] }>('/descope-management/roles').then((r) => r.data.data),
    enabled: open,
    staleTime: 60_000,
  });
  // Only show non-global roles for tenant assignments; global roles for the user level
  const allRoles = rolesData ?? [];
  const tenantRoles = allRoles.filter((r) => r.tenantId !== '');
  const globalRoles = allRoles.filter((r) => r.tenantId === '');

  const [globalRoleNames, setGlobalRoleNames] = useState<string[]>([]);

  const create = useMutation({
    mutationFn: (payload: CreateUserPayload) =>
      api.post<{ data: unknown }>('/descope-management/users', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['descope-management', 'users'] });
      handleClose();
    },
    onError: (err) => {
      setSubmitError(extractErrorMessage(err));
    },
  });

  const handleClose = () => {
    setEmail('');
    setDisplayName('');
    setTenantAssignments([]);
    setGlobalRoleNames([]);
    setSendInvite(true);
    setSubmitError(null);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    create.mutate({
      email: email.trim(),
      displayName: displayName.trim() || undefined,
      roles: globalRoleNames.length > 0 ? globalRoleNames : undefined,
      tenants: tenantAssignments.length > 0 ? tenantAssignments : undefined,
      sendInvite,
    });
  };

  const addTenantRow = () => {
    setTenantAssignments((prev) => [...prev, { tenantId: '', roles: [] }]);
  };

  const removeTenantRow = (idx: number) => {
    setTenantAssignments((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateTenantRow = (idx: number, patch: Partial<TenantAssignment>) => {
    setTenantAssignments((prev) =>
      prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
    );
  };

  const usedTenantIds = new Set(tenantAssignments.map((a) => a.tenantId).filter(Boolean));

  return (
    <FormDialog
      open={open}
      title="Invite User"
      onClose={handleClose}
      onSubmit={handleSubmit}
      loading={create.isPending}
      submitLabel={sendInvite ? 'Send Invitation' : 'Create User'}
      maxWidth="sm"
    >
      {submitError && <Alert severity="error">{submitError}</Alert>}

      <TextField
        label="Email"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        fullWidth
        autoFocus
      />

      <TextField
        label="Display Name"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        fullWidth
      />

      {globalRoles.length > 0 && (
        <Autocomplete
          multiple
          options={globalRoles.map((r) => r.name)}
          value={globalRoleNames}
          onChange={(_, v) => setGlobalRoleNames(v)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Global Roles" placeholder="Select roles..." />
          )}
        />
      )}

      {/* Tenant assignments */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Tenant Assignments</Typography>
          <Tooltip title="Add tenant">
            <IconButton size="small" onClick={addTenantRow}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack spacing={1.5}>
          {tenantAssignments.map((assignment, idx) => {
            const availableRoleNames = tenantRoles
              .filter((r) => !assignment.tenantId || r.tenantId === assignment.tenantId || r.tenantId === '*')
              .map((r) => r.name);

            return (
              <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
                <Select
                  size="small"
                  displayEmpty
                  value={assignment.tenantId}
                  onChange={(e) => updateTenantRow(idx, { tenantId: e.target.value, roles: [] })}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="" disabled>
                    Select tenant
                  </MenuItem>
                  {tenants.map((t) => (
                    <MenuItem
                      key={t.id}
                      value={String(t.externalAuthProviderIdentifier ?? t.id)}
                      disabled={
                        usedTenantIds.has(String(t.externalAuthProviderIdentifier ?? t.id)) &&
                        assignment.tenantId !== String(t.externalAuthProviderIdentifier ?? t.id)
                      }
                    >
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>

                {availableRoleNames.length > 0 ? (
                  <Autocomplete
                    multiple
                    size="small"
                    options={availableRoleNames}
                    value={assignment.roles}
                    onChange={(_, v) => updateTenantRow(idx, { roles: v })}
                    renderTags={(value, getTagProps) =>
                      value.map((option, i) => (
                        <Chip size="small" label={option} {...getTagProps({ index: i })} key={option} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Roles" placeholder="Select roles..." />
                    )}
                    sx={{ flex: 1, minWidth: 140 }}
                  />
                ) : (
                  <Box sx={{ flex: 1 }} />
                )}

                <Tooltip title="Remove">
                  <IconButton size="small" color="error" onClick={() => removeTenantRow(idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            );
          })}
        </Stack>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={sendInvite}
            onChange={(e) => setSendInvite(e.target.checked)}
          />
        }
        label="Send invitation email"
      />
    </FormDialog>
  );
}
